// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useEffect } from 'react';
import './App.css';
import './styles/mobile.css';
import Portal from './components/Portal';
import HUD from './components/HUD';
import MainTabbedInterface from './components/MainTabbedInterface';
import useGlobalHotkey from './hooks/useGlobalHotkey';
import { useAudioInit } from './hooks/useAudioInit';
import { useGameEvents } from './hooks/useGameEvents';
import ContextualHint from './components/ContextualHint';
import contextualTutorial from './utils/game/ContextualTutorial';
import SystemStatus from './components/SystemStatus';
import AlertHistoryModal from './components/AlertHistoryModal';
import ResetConfirmModal from './components/ResetConfirmModal';
import SaveLoadModal from './components/SaveLoadModal';
import CheatCodeHandler from './components/CheatCodeHandler';
import CheatsModal from './components/CheatsModal';
import HelpModal from './components/HelpModal';
import ReputationModal from './components/ReputationModal';
import SettingsModal from './components/SettingsModal';
import AchievementsModal from './components/AchievementsModal';
import AchievementNotification from './components/AchievementNotification';
import ToastContainer from './components/ToastContainer';
import LoginModal from './components/LoginModal';
import DeveloperToolsModal from './components/DeveloperToolsModal';

import LoadingScreen from './components/LoadingScreen';
import apiSaveLoadManager from './utils/system/ApiSaveLoadManager';
import saveLoadManager from './utils/system/SaveLoadManager';
import GameTimeManager from './utils/game/GameTimeManager';
import EventBus, { EVENT_TYPES } from './utils/system/EventBus';
import { useAlerts } from './contexts/AlertContext';
import { useUI, MODAL_TYPES } from './contexts/UIContext';
import { useAuth } from './contexts/AuthContext';
import STORAGE_KEYS from './utils/system/STORAGE_KEYS';

function App() {
  const { isAuthenticated, loading, token, sessionExpired, clearSessionExpired } = useAuth();
  const [systemPower, setSystemPower] = useState(85);
  const [warpSpeed] = useState(0);
  const [currentHint, setCurrentHint] = useState(null);

  const [isLoading, setIsLoading] = useState(true);

  const { currentAlerts, alertHistory, success, error } = useAlerts();

  const {
    isModalOpen,
    closeModalByType,
    openSystemStatus,
    openAlertHistory,
    openResetConfirm,
    openSaveLoad,
    openCheats,
    openHelp,
    openReputation,
    openLogin,
    openDevTools,
    cheatMessage,
    showCheatMessage,
    isPortalActive,
  } = useUI();

  // Open login modal when session expires involuntarily (no hard reload)
  useEffect(() => {
    if (sessionExpired) {
      openLogin();
      clearSessionExpired();
    }
  }, [sessionExpired, openLogin, clearSessionExpired]);

  // Audio initialization and orbit ambient sound management
  useAudioInit();

  // Global EventBus listeners (missions, payday, rank-up, tutorial hints)
  useGameEvents({ success, error, setCurrentHint });

  // Check query params on mount for deep linking
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('login')) openLogin();
    if (params.has('dev')) openDevTools();
  }, [openLogin, openDevTools]);

  // All global hotkeys — simple keys and modifier combos alike — share one
  // listener via useGlobalHotkey, which bakes in the "don't fire while
  // typing in a field, or while a full-screen overlay owns the keyboard"
  // guard once instead of each caller reimplementing it. See
  // useGlobalHotkey.js and OverlayGuard.js.
  useGlobalHotkey({
    'escape': () => {
      if (isModalOpen(MODAL_TYPES.HELP)) closeModalByType(MODAL_TYPES.HELP);
      else if (isModalOpen(MODAL_TYPES.SETTINGS)) closeModalByType(MODAL_TYPES.SETTINGS);
      else if (isModalOpen(MODAL_TYPES.REPUTATION)) closeModalByType(MODAL_TYPES.REPUTATION);
      else if (isModalOpen(MODAL_TYPES.ACHIEVEMENTS)) closeModalByType(MODAL_TYPES.ACHIEVEMENTS);
    },
    'f1': () => openHelp(),
    'f2': () => openReputation(),
    'f3': () => openResetConfirm(),
    'ctrl+l': () => openLogin(),
    'ctrl+shift+d': () => openDevTools(),
    'f12': () => openDevTools(),
    'alt+e': () => {
      if (isModalOpen(MODAL_TYPES.CHEATS)) closeModalByType(MODAL_TYPES.CHEATS);
      else openCheats();
    },
    'ctrl+shift+c': () => {
      if (isModalOpen(MODAL_TYPES.CHEATS)) closeModalByType(MODAL_TYPES.CHEATS);
      else openCheats();
    },
    'meta+shift+c': () => {
      if (isModalOpen(MODAL_TYPES.CHEATS)) closeModalByType(MODAL_TYPES.CHEATS);
      else openCheats();
    },
  });

  // System power fluctuation
  useEffect(() => {
    const interval = setInterval(() => {
      setSystemPower(prev => Math.max(70, Math.min(100, prev + (Math.random() - 0.5) * 5)));
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Start local auto-save
  useEffect(() => {
    saveLoadManager.startAutoSave();
    return () => saveLoadManager.stopAutoSave();
  }, []);

  // Start GameTimeManager
  useEffect(() => {
    GameTimeManager.start();
    return () => GameTimeManager.stop();
  }, []);

  // Welcome alert and first-run voice
  useEffect(() => {
    success('⚡ SHIP SYSTEMS ONLINE - All systems nominal');

    const hasPlayedInitVoice = localStorage.getItem(STORAGE_KEYS.HAS_PLAYED_INIT_VOICE);
    if (!hasPlayedInitVoice) {
      setTimeout(() => {
        EventBus.emit(EVENT_TYPES.SYSTEMS_INITIALIZING);
        localStorage.setItem(STORAGE_KEYS.HAS_PLAYED_INIT_VOICE, 'true');
      }, 2000);
    }
  }, [success]);

  // API auto-save every 2 minutes when authenticated
  useEffect(() => {
    if (!isAuthenticated || !token) return;

    const autoSaveInterval = setInterval(() => {
      apiSaveLoadManager.autoSave(token);
    }, 120000);

    return () => clearInterval(autoSaveInterval);
  }, [isAuthenticated, token]);

  // Load game state from API on login
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    apiSaveLoadManager.loadFromApi(token).catch(() => {});
  }, [isAuthenticated, token]);

  const handleCheatActivated = (cheatInfo) => {
    showCheatMessage(cheatInfo.message);
  };

  const handleTriggerEvent = (eventType) => {
    EventBus.emit(EVENT_TYPES.CHEAT_EVENT_TRIGGERED, { eventType });
    showCheatMessage(`🛠️ Event Triggered: ${eventType}`, 3000);
  };

  if (isLoading) {
    return <LoadingScreen onComplete={() => setIsLoading(false)} />;
  }

  if (loading) {
    return <div className="App"><div className="starfield"></div></div>;
  }

  return (
    <div className="App">
      <div className="starfield"></div>
      <Portal isActive={isPortalActive} />

      <CheatCodeHandler onCheatActivated={handleCheatActivated} />

      {cheatMessage && (
        <div className="cheat-message">
          {cheatMessage}
        </div>
      )}

      <div className="hud-top-area">
        <HUD
          systemPower={systemPower}
          warpSpeed={warpSpeed}
          onOpenSaveLoad={openSaveLoad}
          onOpenReset={openResetConfirm}
          onOpenLogin={() => openLogin()}
        />
      </div>

      <div className="main-content-area">
        <MainTabbedInterface />
      </div>

      {isModalOpen(MODAL_TYPES.SYSTEM_STATUS) && (
        <SystemStatus
          power={systemPower}
          currentAlert={currentAlerts[0]}
          onOpenAlertHistory={openAlertHistory}
          onClose={() => closeModalByType(MODAL_TYPES.SYSTEM_STATUS)}
        />
      )}

      {isModalOpen(MODAL_TYPES.ALERT_HISTORY) && (
        <AlertHistoryModal
          fullAlertHistory={alertHistory}
          onClose={() => closeModalByType(MODAL_TYPES.ALERT_HISTORY)}
          onOpenSystemStatus={() => {
            closeModalByType(MODAL_TYPES.ALERT_HISTORY);
            openSystemStatus();
          }}
        />
      )}

      <ResetConfirmModal
        isOpen={isModalOpen(MODAL_TYPES.RESET_CONFIRM)}
        onConfirm={() => {}}
        onCancel={() => closeModalByType(MODAL_TYPES.RESET_CONFIRM)}
      />

      <SaveLoadModal
        isOpen={isModalOpen(MODAL_TYPES.SAVE_LOAD)}
        onClose={() => closeModalByType(MODAL_TYPES.SAVE_LOAD)}
        onSaveComplete={() => {}}
        onLoadComplete={() => {}}
      />

      <HelpModal
        isOpen={isModalOpen(MODAL_TYPES.HELP)}
        onClose={() => closeModalByType(MODAL_TYPES.HELP)}
      />

      <ReputationModal
        isOpen={isModalOpen(MODAL_TYPES.REPUTATION)}
        onClose={() => closeModalByType(MODAL_TYPES.REPUTATION)}
      />

      <CheatsModal
        isOpen={isModalOpen(MODAL_TYPES.CHEATS)}
        onClose={() => closeModalByType(MODAL_TYPES.CHEATS)}
        onTriggerEvent={handleTriggerEvent}
      />

      <SettingsModal
        show={isModalOpen(MODAL_TYPES.SETTINGS)}
        onClose={() => closeModalByType(MODAL_TYPES.SETTINGS)}
      />

      <AchievementsModal
        show={isModalOpen(MODAL_TYPES.ACHIEVEMENTS)}
        onClose={() => closeModalByType(MODAL_TYPES.ACHIEVEMENTS)}
      />

      <LoginModal
        show={isModalOpen(MODAL_TYPES.LOGIN)}
        onClose={() => closeModalByType(MODAL_TYPES.LOGIN)}
      />

      <DeveloperToolsModal
        show={isModalOpen(MODAL_TYPES.DEV_TOOLS)}
        onClose={() => closeModalByType(MODAL_TYPES.DEV_TOOLS)}
      />

      <AchievementNotification />

      {currentHint && (
        <ContextualHint
          hint={currentHint}
          onDismiss={() => {
            contextualTutorial.markShown(currentHint.id);
            setCurrentHint(null);
          }}
        />
      )}

      <ToastContainer />
    </div>
  );
}

export default App;
